import type { AxiosError } from 'axios'
import { useCallback, useState } from 'react'
import { type ErrorDocument } from '@opallabs/json-api'
import type { HookObject, ServerErrors } from './types'

/**
 * A React hook to help you handle errors returned from a JSON:API endpoint.
 *
 * @see https://jsonapi.org/format/#errors
 */
export function useServerErrors(): HookObject {
  const [serverErrors, setServerErrors] = useState<ServerErrors>({})

  /**
   * Remove specific errors (`errorKeys`) from `serverErrors`.
   *
   * @example
   * unsetServerErrors(['email_address']);
   */
  const unsetServerErrors = useCallback(
    (errorKeys: string[] = []) => {
      const tidiedErrors = {
        ...serverErrors,
      }
      errorKeys.forEach((key) => delete tidiedErrors[key])

      setServerErrors(tidiedErrors)
    },
    [serverErrors]
  )

  /**
   * Automatically extract error information from a JSON:API response and
   * update `serverErrors`.
   *
   * @example
   * import { type ErrorDocument } from '@opallabs/json-api';
   *
   * // Within React component
   * const submitData = useCallback(async () => {
   *   try {
   *     // Submit JSON:API data to endpoint...
   *   } catch (error) {
   *     handleServerError(error as AxiosError<ErrorDocument>);
   *   }
   * }, [handleServerError]);
   *
   * @see https://jsonapi.org/format/#errors
   */
  const handleServerError = useCallback(
    (errorResponse: AxiosError<ErrorDocument>) => {
      const errorData: ErrorDocument['errors'] =
        errorResponse.response?.data.errors || []
      const existingErrors = {
        ...serverErrors,
      }

      const formattedErrors = errorData.reduce(
        (memo, error) =>
          error.code
            ? {
                [error.code]: error.title || '',
                ...memo,
              }
            : memo,
        existingErrors
      )
      setServerErrors(formattedErrors)
    },
    [serverErrors, setServerErrors]
  )

  return {
    handleServerError,
    serverErrors,
    unsetServerErrors,
  }
}
